import React, { useMemo } from "react";
import { CompositeChart } from "@mantine/charts";
import { Box, Title, Text, useMantineTheme, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { DistributorSalesData, SalesMetric } from "types/analyticsTypes";

interface DistributorSalesChartProps {
  data: DistributorSalesData;
  metric: SalesMetric;
  style?: React.CSSProperties;
  title?: string;
}

const DistributorSalesChart: React.FC<DistributorSalesChartProps> = ({
  data,
  metric,
  style,
  title,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const metricLabel =
    metric === SalesMetric.ACCOUNTS
      ? t("analytics.kpi.totalAccountCount")
      : t("analytics.kpi.uniqueDevicesRegistered");

  const chartData = useMemo(() => {
    if (!data?.monthly?.length) return [];

    return data.monthly.map((item, index) => {
      const sum = data.monthly.slice(0, index + 1).reduce((total, curr) => total + curr[metric], 0);
      const runningAverage = sum / (index + 1);

      return {
        date: item.date,
        value: item[metric],
        runningAverage: Number(runningAverage.toFixed(1)),
      };
    });
  }, [data?.monthly, metric]);

  if (!data?.monthly?.length) {
    return (
      <Box my="md">
        {title && <Title order={3}>{title}</Title>}
        <Text c="dimmed">{t("analytics.sales.common.noSalesData")}</Text>
      </Box>
    );
  }

  return (
    <Skeleton visible={!data} style={style}>
      <Box>
        {title && (
          <Title order={3} mb="md">
            {title}
          </Title>
        )}

        <CompositeChart
          h={theme.spacing.longChart}
          data={chartData}
          dataKey="date"
          series={[
            {
              name: "value",
              label: metricLabel,
              color: theme.colors.blue[6],
              type: "bar",
            },
            {
              name: "runningAverage",
              label: t("analytics.sales.common.averageSalesLabel"),
              color: theme.colors.green[7],
              type: "line",
            },
          ]}
          tickLine="x"
          style={style}
          withDots={false}
          curveType="natural"
          withLegend
          legendProps={{ verticalAlign: "top", align: "right" }}
          barProps={{ radius: [4, 4, 0, 0], maxBarSize: 48 }}
          lineProps={{ strokeWidth: 2, strokeDasharray: "6 4" }}
          yAxisProps={{ allowDecimals: false }}
        />
      </Box>
    </Skeleton>
  );
};

export default DistributorSalesChart;
